import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { getExperienceType } from '../../data/experienceTypes';
import { getExperienceTypeV2, getAllExperienceTypesV2, getExperienceTypesByCategory } from '../../data/experienceTypesV2';
import { getExperienceTypeIcon } from '../../utils/experienceTypeIcons';
import { useFamily } from '../../contexts/FamilyContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronRight, faStore, faGift, faCalendar, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import BottomSheet from '../BottomSheet';
import TemplateMarketplace from '../templates/TemplateMarketplace';

export default function ExperienceCreationModal({ isOpen, onClose, selectedTemplate: propSelectedTemplate }) {
  const navigate = useNavigate();
  const { family } = useFamily();
  const [step, setStep] = useState(1); // 1: category, 2: type selection, 3: details
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTemplateMarketplace, setShowTemplateMarketplace] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(propSelectedTemplate || null);
  const [experienceForm, setExperienceForm] = useState({
    title: '',
    date: '',
    guests: ''
  });

  // Handle selectedTemplate prop when modal opens
  useEffect(() => {
    if (propSelectedTemplate && isOpen) {
      const template = propSelectedTemplate;
      setSelectedTemplate(template);
      
      // Auto-select the template's type
      if (template.typeId) {
        setSelectedType(template.typeId);
        
        // Set the category based on template
        const templateType = getExperienceTypeV2(template.typeId) || getExperienceType(template.typeId);
        if (templateType?.category) {
          setSelectedCategory(templateType.category);
        }
        
        // Pre-fill form with template data
        if (template.name) {
          setExperienceForm(prev => ({
            ...prev,
            title: template.name
          }));
        }
        
        // Jump to step 3 (details form) since type is already selected
        setStep(3);
      }
    } else if (!propSelectedTemplate && isOpen) {
      // Reset when opening without a template
      setSelectedTemplate(null);
      setStep(1);
      setSelectedCategory(null);
      setSelectedType(null);
      setExperienceForm({ title: '', date: '', guests: '' });
    }
  }, [propSelectedTemplate, isOpen]);

  const categories = [
    { id: 'lifecycle', name: 'Lifecycle Events', icon: faGift, description: 'Once-in-a-lifetime celebrations' },
    { id: 'annual', name: 'Annual Holidays', icon: faCalendar, description: 'Yearly observances and celebrations' },
    { id: 'cyclical', name: 'Cyclical Events', icon: faArrowsRotate, description: 'Weekly, monthly, or daily routines' }
  ];

  // Get all experience types (both old and new)
  const allV2Types = getAllExperienceTypesV2();
  const v2TypesByCategory = selectedCategory 
    ? getExperienceTypesByCategory(selectedCategory)
    : [];

  // Filter types based on search
  const filteredTypes = selectedCategory && searchQuery
    ? v2TypesByCategory.filter(type => 
        type.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (type.hebrewName && type.hebrewName.includes(searchQuery))
      )
    : v2TypesByCategory;

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setStep(2);
    setSearchQuery('');
  };

  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
    const typeV2 = getExperienceTypeV2(typeId);
    const typeOld = getExperienceType(typeId);
    const experienceType = typeV2 || typeOld;
    
    if (experienceType) {
      const currentYear = new Date().getFullYear();
      setExperienceForm({
        ...experienceForm,
        title: `${experienceType.name} ${currentYear}`
      });
      // Show option to use template or create from scratch
      setStep(3);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    // Pre-fill experience form with template data
    if (template.decisionCards) {
      // Template will be applied when creating experience
    }
    setShowTemplateMarketplace(false);
  };

  const handleCreateExperience = async () => {
    if (!family?.id) {
      toast.error('Please complete onboarding first');
      return;
    }

    if (!selectedType) {
      toast.error('Please select an experience type');
      return;
    }

    if (!experienceForm.title || !experienceForm.date) {
      toast.error('Please fill in title and date');
      return;
    }

    try {
      const typeV2 = getExperienceTypeV2(selectedType);
      const typeOld = getExperienceType(selectedType);
      const experienceType = typeV2 || typeOld;

      // Validate date
      const experienceDate = new Date(experienceForm.date);
      if (isNaN(experienceDate.getTime())) {
        toast.error('Please enter a valid date');
        return;
      }

      // Initialize experience data
      const newExperience = {
        familyId: family.id,
        type: selectedType,
        typeId: selectedType, // For V2 compatibility
        title: experienceForm.title,
        date: Timestamp.fromDate(experienceDate),
        guests: parseInt(experienceForm.guests) || 0,
        status: 'planning',
        currentPhase: typeV2 ? 'explore' : 'exposure', // Use new phase names for V2
        createdAt: Timestamp.now(),
        updatedAt: serverTimestamp(),
      };

      // Initialize based on type version or template
      if (selectedTemplate) {
        // Use template data if available
        if (selectedTemplate.decisionCards) {
          newExperience.decisionCards = selectedTemplate.decisionCards.map(card => ({
            ...card,
            status: card.status || 'to-discuss',
            kanbanColumn: card.kanbanColumn || 'to-discuss',
            gallery: card.gallery || [],
            comments: card.comments || [],
            approvalRequired: card.approvalRequired || family.members || [],
            approvedBy: card.approvedBy || []
          }));
        }
        if (selectedTemplate.tasks) {
          newExperience.tasks = selectedTemplate.tasks.map(task => ({
            ...task,
            completed: false,
            assignedTo: task.assignedTo || []
          }));
        }
        newExperience.createdFromTemplate = selectedTemplate.id;
        newExperience.planningParticipants = family.members || [];
      } else if (typeV2) {
        // V2: Initialize decision cards from type defaults
        newExperience.decisionCards = (typeV2.defaultDecisionCards || []).map(card => ({
          ...card,
          status: 'to-discuss',
          kanbanColumn: 'to-discuss',
          gallery: [],
          comments: [],
          approvalRequired: family.members || [],
          approvedBy: []
        }));
        newExperience.planningParticipants = family.members || [];
        newExperience.tasks = [];
      } else {
        // Old system: Initialize decisions and exposure
        const decisions = {
          basics: {
            status: 'research',
            value: '',
            thread: [],
          },
        };
        (typeOld.defaultDecisions || []).forEach((decision) => {
          decisions[decision.key] = {
            status: decision.status,
            value: '',
            thread: [],
          };
        });

        newExperience.exposure = {
          categories: {},
        };
        newExperience.decisions = decisions;
        newExperience.timeline = typeOld.defaultTimeline || [];
        newExperience.budget = {
          allocated: typeOld.defaultBudget || 500,
          spent: 0,
        };
      }

      const docRef = await addDoc(collection(db, 'projects'), newExperience);
      toast.success('Experience created!');
      onClose();
      setStep(1);
      setSelectedCategory(null);
      setSelectedType(null);
      setSelectedTemplate(null);
      setExperienceForm({ title: '', date: '', guests: '' });
      setSearchQuery('');
      navigate(`/experience/${docRef.id}`);
    } catch (error) {
      console.error('Error creating experience:', error);
      if (error.code === 'permission-denied') {
        toast.error('Permission denied. Please check your authentication.');
      } else if (error.code === 'unavailable') {
        toast.error('Cannot connect to database. Please check your connection.');
      } else {
        toast.error(`Failed to create experience: ${error.message || 'Please try again.'}`);
      }
    }
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
      setExperienceForm({ title: '', date: '', guests: '' });
    } else if (step === 2) {
      setStep(1);
      setSelectedCategory(null);
      setSearchQuery('');
    }
  };

  const handleClose = () => {
    onClose();
    setStep(1);
    setSelectedCategory(null);
    setSelectedType(null);
    setSelectedTemplate(null);
    setExperienceForm({ title: '', date: '', guests: '' });
    setSearchQuery('');
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose} title="Create New Experience" fullScreen={true}>
      <div className="p-6 space-y-6">
        {/* Step 1: Category Selection */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-text-secondary mb-6">
              Choose a category to get started
            </p>
            <div className="grid grid-cols-1 gap-4">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => handleCategorySelect(category.id)}
                  className="bg-white border-2 border-border rounded-2xl p-6 text-left hover:border-primary transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl text-primary">
                        <FontAwesomeIcon icon={category.icon} />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-text-primary mb-1">
                          {category.name}
                        </h3>
                        <p className="text-sm text-text-secondary">
                          {category.description}
                        </p>
                      </div>
                    </div>
                    <FontAwesomeIcon 
                      icon={faChevronRight} 
                      className="text-text-secondary group-hover:text-primary transition-colors"
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Type Selection */}
        {step === 2 && (
          <>
            {/* Template Marketplace Button */}
            <div className="mb-4">
              <button
                onClick={() => setShowTemplateMarketplace(true)}
                className="w-full px-4 py-3 bg-primary/10 border-2 border-primary rounded-xl font-semibold text-primary hover:bg-primary/20 transition-colors flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faStore} />
                Browse Templates
              </button>
            </div>
            <div className="space-y-4">
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={handleBack}
                className="text-2xl text-text-secondary hover:text-primary transition-colors"
              >
                ←
              </button>
              <div>
                <h2 className="font-bold text-xl text-text-primary">
                  {categories.find(c => c.id === selectedCategory)?.name}
                </h2>
                <p className="text-sm text-text-secondary">
                  Select an experience type
                </p>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-secondary"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search experiences..."
                className="w-full pl-12 pr-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
              />
            </div>

            {/* Type Grid */}
            {filteredTypes.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {filteredTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => handleTypeSelect(type.id)}
                    className="bg-white border-2 border-border rounded-xl p-4 text-left hover:border-primary transition-all group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <FontAwesomeIcon 
                            icon={getExperienceTypeIcon(type.icon)} 
                            className="text-2xl text-primary"
                          />
                          <h3 className="font-bold text-text-primary">
                            {type.name}
                          </h3>
                        </div>
                        {type.hebrewName && (
                          <p className="text-sm text-text-secondary mb-2">
                            {type.hebrewName}
                          </p>
                        )}
                        <p className="text-xs text-text-secondary line-clamp-2">
                          {type.description}
                        </p>
                        {type.tier && (
                          <span className="inline-block mt-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-semibold">
                            Tier {type.tier}
                          </span>
                        )}
                      </div>
                      <FontAwesomeIcon 
                        icon={faChevronRight} 
                        className="text-text-secondary group-hover:text-primary transition-colors flex-shrink-0"
                      />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-text-secondary">
                  {searchQuery ? 'No experiences found' : 'No experiences available in this category'}
                </p>
              </div>
            )}
          </div>
          </>
        )}

        {/* Step 3: Details Form */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={handleBack}
                className="text-2xl text-text-secondary hover:text-primary transition-colors"
              >
                ←
              </button>
              <div>
                <h2 className="font-bold text-xl text-text-primary">
                  {(() => {
                    const typeV2 = getExperienceTypeV2(selectedType);
                    const typeOld = getExperienceType(selectedType);
                    return (typeV2 || typeOld)?.name || 'Experience';
                  })()}
                </h2>
                <p className="text-sm text-text-secondary">
                  Add basic details
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={experienceForm.title}
                  onChange={(e) => setExperienceForm({ ...experienceForm, title: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-all"
                  placeholder="Experience title"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Date *
                </label>
                <input
                  type="date"
                  value={experienceForm.date}
                  onChange={(e) => setExperienceForm({ ...experienceForm, date: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Number of Guests (Optional)
                </label>
                <input
                  type="number"
                  value={experienceForm.guests}
                  onChange={(e) => setExperienceForm({ ...experienceForm, guests: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-all"
                  placeholder="12"
                  min="0"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleBack}
                  className="flex-1 px-6 py-3 rounded-xl font-semibold bg-white border-2 border-border text-text-secondary hover:border-primary hover:text-primary transition-all"
                >
                  Back
                </button>
                <button
                  onClick={handleCreateExperience}
                  className="flex-1 px-6 py-3 rounded-xl font-semibold bg-primary text-white hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover"
                >
                  Create Experience
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Template Marketplace */}
        {showTemplateMarketplace && (
          <TemplateMarketplace
            experienceTypeId={selectedType}
            onSelectTemplate={handleTemplateSelect}
            onClose={() => setShowTemplateMarketplace(false)}
          />
        )}
      </div>
    </BottomSheet>
  );
}
